import { Tab, Tabs } from "@mui/material";
import { Box } from "@mui/system";
import i18next from "i18next";
import React, { useCallback, useContext, useEffect, useState } from "react";
import EAButton from "../../../components/common/EAButton";
import EATabPanel from "../../../components/common/EATabPanel";
import PanelIndued from "../../../components/thirdparties/PanelIndued";
import { ERROR, Subscription } from "../../../services/common/Constants";
import {
  DueDiligenceStatus,
  isInduedDone,
} from "../../../services/common/InduedUtils";
import {
  launchThirdpartyNewDueligence,
  refreshThirdpartyIndued,
} from "../../../services/edge/ThirdpartyService";
import { Context } from "../../../states/Store";
import useStyles from "../../../style/js-style/containers/thirdparties/sheet/ThirdpartyRisksStyle";
import EAIcon from "../../../components/common/EAIcon";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "../../../services/common/RolesUtils";
import { theme } from "../../../style/globalStyle";
import useMediaQuery from "@mui/material/useMediaQuery";
import PanelTransparency from "components/thirdparties/PanelTransparency";
import clsx from "clsx";
import { hasOption } from "services/common/Utils";
import PanelFinancialStrength from "components/thirdparties/PanelFinancialStrength";
import { getEvolutionScoreDataBlocks } from "services/edge/DataBlocksService";
import PanelOwlin from "components/thirdparties/owlin/PanelOwlin";
import AdditionalMessageAlert from "components/AdditionalMessageAlert";
import _, { isEmpty, isNull } from 'lodash';
import EcovadisPanel from "components/thirdparties/ecovadis/EcovadisPanel";
import PanelESG from "components/thirdparties/PanelESG";
import SecurityRatingPanel from "components/thirdparties/security-rating/SecurityRatingPanel";
import CreditsafePanel from "components/thirdparties/creditsafe/CreditsafePanel";

function a11yProps(index) {
  return {
    id: `tp-risks-tab-${index}`,
    "aria-controls": `tp-risks-tabpanel-${index}`,
    value: index,
  };
}

const ThirdpartyRisksActions = ({ indued, onInduedChange, thirdPartyId }) => {
  const [state, dispatch] = useContext(Context);
  const { classes } = useStyles();
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);
  const mediaQueryXL = useMediaQuery(theme.breakpoints.up("1900"));
  const mediaQueryL = useMediaQuery(theme.breakpoints.up("1480"));

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  const refreshIndued = async () => {
    setIsRefreshing(true);
    try {
      await refreshThirdpartyIndued(state.account.id, thirdPartyId, dispatch);
      setIsRefreshing(false);
      onInduedChange && onInduedChange();
    } catch (e) {
      setIsRefreshing(false);
      if (!e?.message.messages?.error) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: "INTERNAL_ERROR" },
        });
      }
    }
  };

  const openFPM = (fpmUrl) => {
    window.open(fpmUrl, "_blank", "noopener, noreferrer")?.focus();
  };

  const launchDueDiligence = async () => {
    setIsLaunching(true);
    try {
      await launchThirdpartyNewDueligence(
        state.account.id,
        thirdPartyId,
        dispatch
      );
      setIsLaunching(false);
      onInduedChange && onInduedChange();
    } catch (e) {
      setIsLaunching(false);
      if (!e?.message.messages?.error) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: "INTERNAL_ERROR" },
        });
      }
    }
  };

  return (
    <div
      style={{
        position: "relative",
        gap: "20px",
        display: "flex",
        marginTop: "10px",
        marginRight: "8px",
        float: "right"
      }}
    >
      {indued?.due_diligence?.status === DueDiligenceStatus.FPM_IN_PROGRESS && (
        <EAButton
          size="small"
          onClick={() => openFPM(indued.due_diligence.fpm_url)}
          colorVariant={"warningLight"}
          disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_REFRESH_INDUED_SOURCE)}
          customStyle={{ fontSize: mediaQueryL ? "14px" : mediaQueryXL ? "16px" : "12px"}}
          content={
            <>
              <EAIcon icon="false-positif" className={classes.mr6} />
              <span>{i18next.t("indued.fpm")}</span>
            </>
          }
        />
      )}
      {indued?.created_date && !isInduedDone(indued) && (
        <EAButton
          size="small"
          onClick={() => refreshIndued()}
          disabled={isRefreshing || !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_REFRESH_INDUED_SOURCE)}
          customStyle={{ fontSize: mediaQueryL ? "14px" : mediaQueryXL ? "16px" : "12px"}}
          content={
            <>
              <EAIcon icon="refresh" className={classes.mr6} />
              <span>{i18next.t("refresh")}</span>
            </>
          }
        />
      )}
      {isInduedDone(indued) && (
        <EAButton
          size="small"
          onClick={() => launchDueDiligence()}
          disabled={isLaunching || !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_REFRESH_INDUED_SOURCE)}
          colorVariant={"primaryLight"}
          customStyle={{ fontSize: mediaQueryL ? "14px" : mediaQueryXL ? "16px" : "12px"}}
          content={
            <>
              <EAIcon icon="newEnrichment" className={classes.mr6} />
              <span>{i18next.t("indued.newDueDiligence")}</span>
            </>
          }
        />
      )}
    </div>
  );
};

function getRisksTabs({
  state,
  hasAnyOfPrivileges,
  risks
}) {

  return [
    risks?.indued && {
      label: i18next.t("indued.label"),
      name: "indued",
      index: 1,
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "TRANSPARENCY") && {
      label: i18next.t("transparency.label"),
      name: "transparency",
      index: 2,
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "FINANCIALSTRENGTH") && {
      label: i18next.t("financialStrength.label"),
      name: "datablocks_financial_strength",
      index: 3,
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "OWLIN") && {
      label: i18next.t("owlin.label"),
      name: "owlin",
      index: 4,
      width: "160px",
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_OWLIN_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "ECOVADIS") && {
      label: i18next.t("ecovadis.EVratings"),
      name: "ecovadis",
      index: 5,
      width: "160px",
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "ESG") && {
      label: i18next.t("ESG.label"),
      name: "datablocks_esg",
      index: 6,
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "SECURITY_RATING") && {
      label: i18next.t("securityRating.label"),
      name: "security_rating",
      index: 7,
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE),
    },
    hasOption(state.accounts, state.account.id, "CREDITSAFE") && {
      label: i18next.t("creditsafe.label"),
      name: "credit_safe",
      index: 8,
      disabled: !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE),
    },
  ].filter(Boolean);
}


function ThirdpartyRisks({ risks, onRiskChange = () => { }, thirdPartyId, isUnmanaged, isArchived }) {
  const { classes } = useStyles();
  const [state,] = useContext(Context);
  const [induedData, setInduedData] = useState();
  const [transparencyData, setTransparencyData] = useState();
  const [financialStrengthData, setFinancialStrengthData] = useState();
  const [esgData, setEsgData] = useState();
  const [risksTabs, setRisksTabs] = React.useState();
  const [tabIndex, setTabIndex] = React.useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);
  const up1900 = useMediaQuery(theme.breakpoints.up("1900"));

  useEffect(() => {
    setInduedData(risks?.indued);
    if (hasOption(state.accounts, state.account.id, "TRANSPARENCY")) {
      setTransparencyData(risks?.transparency);
    }
    if (hasOption(state.accounts, state.account.id, "FINANCIALSTRENGTH")) {
      setFinancialStrengthData(risks?.datablocks_financial_strength);
    }
    if (hasOption(state.accounts, state.account.id, "ESG")) {
      setEsgData(risks?.datablocks_esg);
    }
  }, [risks, state.accounts, state.account]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const risk = urlParams.get('risk');
    if (risk === "indued") {
      setTabIndex(1);
    }
  }, []);

  const tabClassName = (index) =>
    classes.tab +
    " " +
    (!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR) ? classes.tabDisabled : tabIndex === index ? classes.selectedTab : classes.unselectedTab)
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };


  const callEvolutionDataBlocks = useCallback(async (datablocks, setEvolution) => {
    try {
      const reponse = await getEvolutionScoreDataBlocks(state.account.id, thirdPartyId, datablocks);
      const evolution = reponse.content;
      setEvolution(evolution ? evolution : []);
    } catch (error) {
      console.log(error);
    }
  }, [state.account.id, thirdPartyId]);

  // Récupération de l'évolution du score de santé financière au cours du temps (en se basant sur le monitoring du score)
  const [evolutionFinancialStrength, setEvolutionFinancialStrength] = useState([]);
  useEffect(() => {
    if (financialStrengthData && thirdPartyId) {
      callEvolutionDataBlocks("financialStrength", setEvolutionFinancialStrength);
    }
  }, [callEvolutionDataBlocks, financialStrengthData, thirdPartyId])

  // Récupération de l'évolution du score de D&B ESG au cours du temps (en se basant sur le monitoring du score)
  const [evolutionESG, setEvolutionESG] = useState([]);
  useEffect(() => {
    if (esgData && thirdPartyId) {
      callEvolutionDataBlocks("ESG", setEvolutionESG);
    }
  }, [callEvolutionDataBlocks, esgData, thirdPartyId])

  useEffect(() => {
    const risksTabsObj = getRisksTabs({state, hasAnyOfPrivileges, risks});
    setRisksTabs(risksTabsObj)

    // Définie la tab sélectionnée (qui contient des données) au premier rendu
    if (risks && !isLoaded){
      let firstTabToDisplay = null;
      let indexToTry = 0;

      while (firstTabToDisplay === null){
        // Si on ne trouve aucun onglet avec des données, affiche la premiere
        if (indexToTry >= risksTabsObj?.length) {
          firstTabToDisplay = 0;
        }

        // Check des risks avec des values
        switch (risksTabsObj[indexToTry]?.name) {
          case "indued" :
            if (risks?.indued && risks?.indued.created_date && risks?.indued.due_diligence){
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "transparency" :
            if (!_.isEmpty(risks?.transparency)){
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "datablocks_financial_strength" :
            if (risks?.datablocks_financial_strength && !isEmpty(risks?.datablocks_financial_strength)){
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "owlin" :
            if (risks?.owlin && !isEmpty(risks?.owlin)){
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "ecovadis" :
            if (risks?.ecovadis && !Object.values(risks?.ecovadis).every(isNull)) {
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "datablocks_esg" :
            if (risks?.datablocks_esg && !isEmpty(risks?.datablocks_esg)) {
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "security_rating" :
            if (risks?.security_rating && !Object.values(risks?.security_rating).every(isNull) ){
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          case "credit_safe" :
            if (risks?.credit_safe && !Object.values(risks?.credit_safe).every(isNull) ){
              firstTabToDisplay = risksTabsObj[indexToTry]?.index;
            }
            break;
          default :
            break;
        }
        indexToTry++;
      }

      setTabIndex(firstTabToDisplay || risksTabsObj?.[0]?.index);
    }
    risks && setIsLoaded(true);
  }, [state, hasAnyOfPrivileges, risks, isLoaded])

  return (
    <>
      {/* Tab Titles */}
      <div className={classes.infoPanel}>
        <Box className={classes.tabsWrapper}>
          <div className={classes.lineOnTabs} />
          {tabIndex && (
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="risks tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {risksTabs?.map(({ label, index, width, disabled }) => (
                <Tab
                  key={index}
                  label={label}
                  {...a11yProps(index)}
                  className={clsx(tabClassName(index), classes.dt)}
                  style={{ fontSize: up1900 ? "16px" : "14px", ...(width ? { width } : {}) }}
                  disabled={disabled}
                />
              ))}
            </Tabs>
          )}
        </Box>

        {/* Panels */}
        <div className={classes.panelWrapper}>
          {/* indueD */}
          {induedData && (
            <EATabPanel value={tabIndex} index={1} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE) && (
                <>
                  {isUnmanaged ? (
                    <div className={classes.rootNoData}>
                      <span className={classes.noDataText}>{i18next.t(`unmanagedThirdParty.optionNotAvailable`) + " " + i18next.t(`indued.label`)}</span>
                    </div>
                  )
                    :
                    <>
                      <ThirdpartyRisksActions
                        indued={induedData}
                        onInduedChange={onRiskChange}
                        thirdPartyId={thirdPartyId}
                      />
                      <PanelIndued indued={induedData} />
                      {!isEmpty(induedData.error) && (
                        <div style={{ marginLeft: "8px" }}>
                          <AdditionalMessageAlert
                            icon="warning"
                            message={i18next.t(`indued.errorMessage.${induedData.error}`)}
                          />
                        </div>
                      )}
                    </>
                  }
                </>
              )}
            </EATabPanel>
          )}

          {/* Transparency Data */}
          {transparencyData && (
            <EATabPanel value={tabIndex} index={2} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE) &&
                <PanelTransparency transparency={transparencyData} />
              }
            </EATabPanel>
          )}

          {/* Santé financière */}
          {financialStrengthData &&
            <EATabPanel value={tabIndex} index={3} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE) &&
                <>
                  {(isUnmanaged || isArchived) ?
                    <div className={classes.rootNoData}>
                      {isUnmanaged ?
                      <span className={classes.noDataText}>{i18next.t(`unmanagedThirdParty.optionNotAvailable`) + " " + i18next.t(`financialStrength.label`)}</span>
                        :
                        <span className={classes.noDataText}>{i18next.t(`thirdpartySheet.thirdpartyStoppedNoEditing`)}</span>
                      }
                      </div>
                    :
                    <PanelFinancialStrength financialStrength={financialStrengthData} thirdPartyId={thirdPartyId}
                      evolutionScore={evolutionFinancialStrength?.evolutionScore} onRiskChange={onRiskChange} />
                  }
                </>
              }
            </EATabPanel>
          }
          {hasOption(state.accounts, state.account.id, "OWLIN") &&
            <EATabPanel value={tabIndex} index={4} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_OWLIN_SOURCE) &&
                <>
                {isArchived ?
                    <div className={classes.rootNoData}>
                        <span className={classes.noDataText}>{i18next.t(`thirdpartySheet.thirdpartyStoppedNoEditing`)}</span>
                      </div>
                    :  <PanelOwlin thirdPartyId={thirdPartyId} />
                }
              </>
              }
            </EATabPanel>
          }
          {/* Ecovadis */}
          {hasOption(state.accounts, state.account.id, "ECOVADIS") && risks?.ecovadis &&
            <EATabPanel value={tabIndex} index={5} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE) &&
                <>
                  {isUnmanaged ?
                    <div className={classes.rootNoData}>
                      <span className={classes.noDataText}>{i18next.t(`unmanagedThirdParty.optionNotAvailable`) + " " + i18next.t(`ecovadis.EVratings`)}</span>
                    </div>
                    :<EcovadisPanel
                    evData={{
                      ...risks.ecovadis.ev_data,
                      error: risks.ecovadis.error,
                      error_message: risks.ecovadis.error_message,main_integration_id: risks.ecovadis.main_integration_id
                    }}
                    onRiskChange={onRiskChange}
                  />
                }
              </>
              }
            </EATabPanel>
          }
          {/* D&B ESG */}
          {esgData &&
            <EATabPanel value={tabIndex} index={6} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) &&
                <>
                  {(isUnmanaged || isArchived) ?
                    <div className={classes.rootNoData}>
                      {isUnmanaged ?
                      <span className={classes.noDataText}>{i18next.t(`unmanagedThirdParty.optionNotAvailable`) + " " + i18next.t(`ESG.label`)}</span>
                        :
                        <span className={classes.noDataText}>{i18next.t(`thirdpartySheet.thirdpartyStoppedNoEditing`)}</span>
                      }
                      </div>
                    :<PanelESG dataESG={esgData} thirdPartyId={thirdPartyId}
                            evolutionScore={evolutionESG} onRiskChange={onRiskChange} />}
                </>
              }
            </EATabPanel>
          }

          {/* Security Rating */}
          {hasOption(state.accounts, state.account.id, "SECURITY_RATING") &&
            <EATabPanel value={tabIndex} index={7} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE) &&
                <SecurityRatingPanel data={risks?.security_rating} thirdPartyId={thirdPartyId} onRiskChange={onRiskChange}/>
              }
            </EATabPanel>
          }

          {/* Creditsafe */}
          {hasOption(state.accounts, state.account.id, Subscription.CREDITSAFE) &&
            <EATabPanel value={tabIndex} index={8} style={{ height: "100%" }}>
              {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE) &&
                <>
                  {(isUnmanaged || isArchived) ?
                    <div className={classes.rootNoData}>
                      {isUnmanaged ?
                        <span className={classes.noDataText}>{i18next.t(`unmanagedThirdParty.optionNotAvailable`) + " " + i18next.t(`creditsafe.label`)}</span>
                        :
                        <span className={classes.noDataText}>{i18next.t(`thirdpartySheet.thirdpartyStoppedNoEditing`)}</span>
                      }
                    </div>
                    :
                    <CreditsafePanel data={risks?.creditsafe} thirdPartyId={thirdPartyId} onRiskChange={onRiskChange} />
                  }
                </>
              }
            </EATabPanel>
          }
        </div>
      </div>
    </>
  );
}

export default ThirdpartyRisks;
